/* eslint-disable no-console */
/**
 * Sentry Error Tracking Configuration
 *
 * Initialize Sentry for error tracking in production.
 * Set REACT_APP_SENTRY_DSN in your .env file to enable.
 * 
 * Get your DSN from: https://sentry.io/settings/[your-org]/projects/[your-project]/keys/
 */

import * as Sentry from "@sentry/react";

// Only initialize Sentry if DSN is provided
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;

if (SENTRY_DSN && process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.NODE_ENV,
    integrations: [
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Performance Monitoring — 20% sample rate to control costs at scale
    tracesSampleRate: 0.2,
    
    // Session Replay (optional - requires Sentry plan with replay)
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    
    // Filter out sensitive data
    beforeSend(event, _hint) {
      // Remove sensitive data from events
      if (event.request?.data) {
        // Remove API keys, tokens, etc. from request data
        const sensitiveKeys = ['password', 'token', 'apiKey', 'secret', 'creditCard', 'ssn'];
        sensitiveKeys.forEach(key => {
          if (event.request && event.request.data && typeof event.request.data === 'object') {
            delete (event.request.data as Record<string, unknown>)[key];
          }
        });
      }
      
      // Remove sensitive data from user context
      if (event.user) {
        // Keep only safe user info
        event.user = {
          id: event.user.id,
          email: event.user.email ? event.user.email.replace(/(.{2})(.*)(@.*)/, '$1***$3') : undefined,
        };
      }
      
      return event;
    },
    
    // Ignore certain errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'atomicFindClose',
      'fb_xd_fragment',
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      'conduitPage',
      // Network errors that are expected
      'NetworkError',
      'Failed to fetch',
      'Network request failed',
      // Firebase errors that are handled
      'auth/user-not-found',
      'auth/wrong-password',
      'auth/email-already-in-use',
    ],
    
    // Don't send errors from localhost (only relevant if somehow in production mode with localhost)
    denyUrls: [
      /localhost/,
      /127\.0\.0\.1/,
    ],
  });
  
  // Make Sentry available globally for logger service
  if (typeof window !== 'undefined') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).Sentry = Sentry;
  }
  
  console.log('✅ Sentry initialized for error tracking');
} else if (process.env.NODE_ENV === 'production' && !SENTRY_DSN) {
  console.warn('⚠️ Sentry DSN not configured. Error tracking disabled. Set REACT_APP_SENTRY_DSN in .env');
}

// Export Sentry for use in other files
export default Sentry;

